import { create } from "zustand";
import {
  ChatHistory,
  ChatHistoryMetadata,
  ChatMessage,
  TokenUsage,
  AttemptData,
  ToolCall,
  ToolCallExecution,
  Usage,
} from "@webuild-ai/admin/models/components";
import { WeBuildAiAdmin } from "@webuild-ai/admin";
import { WebuildAiPlatform } from "@webuild-ai/agent-platform";
import { WorkflowConfig } from "@webuild-ai/agent-platform/models/components";
import { HTTPValidationError } from "@webuild-ai/agent-platform/models/errors";

import { logSSEPerformanceMetrics } from "@/utils/ssePerformance";
import { InvokeMessage } from "@/utils/chat/workflowExecution";
import {
  SSEEventType,
  isCompletionEvent,
  extractMessageId,
  extractMessageType,
  isStreamingEvent,
  isMessageCompletedEvent,
  MessageType,
} from "@/types/sse";
import { detectArtefacts } from "@/utils/ArtefactDetector";
import { Artefact, ArtefactType } from "@/types/artefacts";
import { Citation } from "@/types/citations";
import { logger } from "@/telemetry";
import {
  isCitationList,
  isMessageType,
  isRecord,
  isSystemChatter,
  readNumber,
  readString,
  readToolCallExecutions,
  readToolCalls,
  readUsageOrZero,
} from "@/utils/chat/streamPayload";

import { useArtefactStore } from "./ArtefactStore";
import { useChatHistoryStore } from "./ChatHistoryStore";

// Constants to maintain referential equality for empty arrays
const EMPTY_TOOL_EXECUTIONS: never[] = [];
const EMPTY_TOOL_CALLS: never[] = [];

/**
 * Extract graph artefacts from tool_call_executions (side-channel data).
 * Tools like `visualise_graph` attach graph data as `args.graph_artefact`
 * rather than in the message content body.
 */
export function extractGraphArtefactsFromToolExecs(
  toolExecs: Array<{ name: string; args?: Record<string, any> }>,
  messageId: string,
): Artefact[] {
  const artefacts: Artefact[] = [];

  toolExecs.forEach((exec, i) => {
    const graphData = typeof exec.args === "object" && exec.args?.graph_artefact;

    if (graphData) {
      const nodeCount = graphData.node_count ?? graphData.graph?.nodes?.length ?? 0;
      const edgeCount = graphData.edge_count ?? graphData.graph?.edges?.length ?? 0;

      artefacts.push({
        id: `${messageId}-graph-${exec.name}-${i}`,
        type: ArtefactType.GRAPH,
        title: graphData.title || "Graph Visualisation",
        content: JSON.stringify(graphData),
        messageId,
        timestamp: new Date().toISOString(),
        metadata: {
          nodeCount,
          edgeCount,
          estimatedSize: `${nodeCount} nodes, ${edgeCount} edges`,
        },
      });
    }
  });

  return artefacts;
}

/**
 * Extract file artefacts from tool_call_executions (side-channel).
 * Similar to graph extraction — looks for `file_artefact` in tool exec args.
 */
export function extractFileArtefactsFromToolExecs(
  toolExecs: Array<{ name: string; args?: Record<string, any> }>,
  messageId: string,
): Artefact[] {
  const artefacts: Artefact[] = [];

  toolExecs.forEach((exec, i) => {
    const fileData =
      exec.args && typeof exec.args === "object" ? exec.args.file_artefact : undefined;

    if (!fileData) return;

    // Only emit an artefact for a well-formed payload. A truthy-but-malformed
    // file_artefact (a non-object, or one missing filename/content_base64) would
    // otherwise render a dead file card that does nothing when clicked, so drop
    // it with a diagnostic instead of silently manufacturing a broken artefact.
    if (
      typeof fileData !== "object" ||
      typeof fileData.filename !== "string" ||
      typeof fileData.content_base64 !== "string"
    ) {
      logger.warn("Skipping malformed file_artefact in tool execution", {
        tool: exec.name,
        messageId,
      });

      return;
    }

    artefacts.push({
      id: `${messageId}-file-${exec.name}-${i}`,
      type: ArtefactType.FILE,
      title: fileData.filename || "Generated File",
      content: JSON.stringify(fileData),
      messageId,
      timestamp: new Date().toISOString(),
    });
  });

  return artefacts;
}

/**
 * Extended ChatMessage type that includes messageType for differentiating
 * between user, assistant, and system messages in the UI.
 */
export interface ExtendedChatMessage extends ChatMessage {
  messageType?: MessageType;
}

// ============================================================================
// SSE output events
// ============================================================================

/** The fields of a streaming output event that the handlers act on. */
interface OutputPayload {
  msg: Record<string, unknown>;
  data: string;
  agent: string | undefined;
  messageId: string;
  messageType: MessageType;
  isRegenerationEvent: boolean;
  chunkNumber: number | null;
  totalChunks: number | null;
}

/**
 * Parse an output event. Returns null when the JSON is unparseable, and
 * throws when the event carries no message ID.
 */
function parseOutputPayload(event: MessageEvent): OutputPayload | null {
  let parsed: unknown;

  try {
    parsed = JSON.parse(event.data);
  } catch (error) {
    logger.error("Failed to parse SSE message", error);

    return null;
  }

  const messageId = extractMessageId(parsed);

  // A body with no message ID, including a JSON null, is rejected before any
  // of its fields are read. Each field is then read only if it has the type
  // the handlers rely on, since the body is unvalidated JSON.
  if (!messageId || !isRecord(parsed)) {
    throw new Error("Message ID is required");
  }

  return {
    msg: parsed,
    data: readString(parsed.data) || readString(parsed.content) || "",
    agent: readString(parsed.agent),
    messageId,
    messageType: extractMessageType(parsed),
    isRegenerationEvent: Boolean(parsed.is_regeneration),
    chunkNumber: readNumber(parsed.chunk_number) || null,
    totalChunks: readNumber(parsed.total_chunks) || null,
  };
}

/** Log the receipt of an output event, and of the first chunk of a message. */
function logOutputReceived(payload: OutputPayload): void {
  const { msg, data, messageId, isRegenerationEvent, chunkNumber, totalChunks } = payload;

  // Enhanced logging for message flow analysis
  logger.info(`📨 SSE MESSAGE RECEIVED`, {
    messageId,
    chunkNumber: String(chunkNumber),
    totalChunks: String(totalChunks),
    isRegenerationEvent: String(isRegenerationEvent),
    dataLength: String(data?.length),
    dataPreview: data?.substring(0, 100) || "[empty]",
    eventType: String(msg.type || "content"),
  });

  // SPECIAL TRACKING for chunk 1 - this should help us see what happens to the first chunk
  if (chunkNumber === 1 || (data && data.length > 0 && !chunkNumber && isRegenerationEvent)) {
    logger.info(`🚨 FIRST CHUNK TRACKING`, {
      messageId,
      chunkNumber: String(chunkNumber || "unknown"),
      isRegenerationEvent: String(isRegenerationEvent),
      dataLength: String(data?.length),
      fullData: String(data),
    });
  }
}

/**
 * Log the receipt of an output event and decide whether to drop it: a
 * streaming event with no data, a system message, or an empty message that is
 * not a completion event (informational/keep-alive events).
 */
function shouldIgnoreOutput(payload: OutputPayload): boolean {
  const { msg, data, messageId } = payload;

  // Validate streaming events have required data
  if (isStreamingEvent(msg) && !data) {
    logger.warn(`⚠️ Streaming event missing data`, {
      messageId,
      type: String(payload.msg.type),
    });

    return true;
  }

  logOutputReceived(payload);

  if (data && isSystemChatter(data)) {
    logger.info(`🔧 Filtered out system message: ${data.substring(0, 100)}...`);

    return true;
  }

  // Skip empty content messages, BUT preserve completion events even if they have no content
  if (!data && !isCompletionEvent(msg)) {
    logger.info(`🔇 Skipping empty SSE message for ${messageId}`);

    return true;
  }

  return false;
}

/** Log that a regeneration chunk arrived, and whether it is the first one. */
function logRegenerationChunk(
  message: ChatMessage,
  payload: OutputPayload,
  currentContent: string,
  isFirstChunk: boolean,
): void {
  const { data, messageId, isRegenerationEvent } = payload;

  // Debug logging for regeneration streaming
  logger.info(`🔄 REGENERATION STREAMING DETECTED`, {
    messageId,
    isRegenerationEvent: String(isRegenerationEvent),
    attempts: String(message.attempts),
    status: String(message.status),
    currentContentLength: String(currentContent.length),
    newDataLength: String(data?.length || 0),
  });

  logger.info(`🔍 FIRST CHUNK DETECTION`, {
    messageId,
    contentIsEmpty: String(isFirstChunk),
    isRegenerationEvent: String(isRegenerationEvent),
    contentLength: String(currentContent?.length),
    dataLength: String(data?.length),
  });
}

/** Append a later chunk of a regeneration to the content received so far. */
function appendRegenerationChunk(payload: OutputPayload, currentContent: string): string {
  const { data, chunkNumber } = payload;
  const newContent = currentContent + data;

  logger.info(`🔄 REGENERATION: Appending chunk`, {
    chunkNumber: String(chunkNumber),
    currentLength: String(currentContent?.length),
    appendingLength: String(data?.length),
    newTotalLength: String(newContent.length),
  });

  return newContent;
}

/** Log the first chunk of a regeneration replacing the content entirely. */
function logRegenerationReplacement(
  message: ChatMessage,
  payload: OutputPayload,
  currentContent: string,
): void {
  const { data, messageId, chunkNumber } = payload;

  logger.info(`🔄 REGENERATION: First chunk detected, REPLACING content`, {
    chunkNumber: String(chunkNumber),
    oldContentLength: String(currentContent?.length),
    newContentLength: String(data?.length),
  });

  // CRITICAL: Special logging for chunk 1 replacement
  if (chunkNumber === 1) {
    logger.info(`🚨 CHUNK 1 REPLACEMENT HAPPENING`, {
      messageId,
      beforeContentLength: String(currentContent?.length || 0),
      afterContentLength: String(data?.length || 0),
      messageStatus: String(message.status),
      attempts: String(message.attempts),
    });
  }
}

/** Log that a chunk's message is in the chat, and where it stands. */
function logMessageFound(message: ChatMessage, messageId: string): void {
  logger.info(`📝 Found message to update`, {
    messageId,
    currentStatus: String(message.status),
    attempts: String(message.attempts),
    contentLength: String(typeof message.content === "string" ? message.content.length : 0),
  });
}

/** Log a content update applied to a streaming message. */
function logContentUpdate(message: ChatMessage, newContent: string, payload: OutputPayload) {
  const { messageId, chunkNumber } = payload;

  // CRITICAL: Log every content update with details
  logger.info(`📝 CONTENT UPDATE APPLIED`, {
    messageId,
    chunkNumber: String(chunkNumber),
    oldContentLength: String(typeof message.content === "string" ? message.content.length : 0),
    newContentLength: String(newContent.length),
    isFirstChunk: String(chunkNumber === 1),
  });
}

/** An assistant message reused for a regenerated reply, under its new ID. */
function regeneratedMessage(messageToReplace: ChatMessage, payload: OutputPayload): ChatMessage {
  return {
    ...messageToReplace,
    id: payload.messageId, // Update to new message ID
    content: payload.data || "", // Use the incoming data
    status: "streaming",
    timestamp: new Date().toISOString(),
    attempts: messageToReplace.attempts || 1,
    currentAttempt: messageToReplace.currentAttempt || 1,
  };
}

/**
 * The content a streaming message should hold after this chunk. Normal
 * streaming appends. A regeneration (flagged by the backend, or a message with
 * more than one attempt) replaces the content on its first chunk and appends
 * after that.
 */
function computeStreamedContent(message: ChatMessage, payload: OutputPayload): string {
  const { data, isRegenerationEvent } = payload;
  const isRegeneration = isRegenerationEvent || (message.attempts && message.attempts > 1);
  const currentContent = typeof message.content === "string" ? message.content : "";

  if (!isRegeneration) {
    // Normal streaming - always append
    const newContent = currentContent + data;

    logger.info(`📝 Normal streaming: Appending chunk, new length: ${newContent.length}`);

    return newContent;
  }

  const isFirstChunk = currentContent === "";

  logRegenerationChunk(message, payload, currentContent, isFirstChunk);

  if (!isFirstChunk) return appendRegenerationChunk(payload, currentContent);

  logRegenerationReplacement(message, payload, currentContent);

  return data;
}

// ============================================================================
// SSE finalisation
// ============================================================================

/** A chat history as the API returns it, which carries the workflow type the client omits. */
type LoadedChatHistory = ChatHistory & { workflowType?: string | null };

/** The fields of a completion event that finalising a message acts on. */
interface FinalisePayload {
  data: string;
  agent: string | undefined;
  messageId: string;
  messageType: MessageType;
  citations: Citation[] | undefined;
  toolCallExecutions: ToolCallExecution[] | undefined;
  toolCalls: ToolCall[] | undefined;
  usage: Usage;
}

/**
 * Parse a completion event. For message_completed events the message itself is
 * in the `data` field. Returns null when the JSON is unparseable, and throws
 * when the event carries no message ID.
 */
function parseFinalisePayload(event: MessageEvent): FinalisePayload | null {
  let msg: unknown;

  try {
    msg = JSON.parse(event.data);
  } catch (error) {
    logger.error("Failed to parse SSE finalize message", error);

    return null;
  }

  const messageData: unknown = isMessageCompletedEvent(msg) && msg.data ? msg.data : msg;
  const messageId = extractMessageId(messageData);

  // Fields are read only once the body is known to be an object with an ID,
  // and each only if it has the type the handlers rely on.
  if (!messageId || !isRecord(messageData)) {
    throw new Error("Message ID is required");
  }

  return {
    data: readString(messageData.data) || "",
    agent: readString(messageData.agent),
    messageId,
    messageType: extractMessageType(msg),
    // Extract citations if present (structured citations feature)
    citations: readCitations(msg) || readCitations(messageData),
    toolCallExecutions: readToolCallExecutions(messageData.tool_call_executions),
    toolCalls: readToolCalls(messageData.tool_calls),
    usage: readUsageOrZero(messageData),
  };
}

/** The citations a payload carries, or `undefined` when it has none in a usable form. */
function readCitations(payload: unknown): Citation[] | undefined {
  const citations = isRecord(payload) ? payload.citations : undefined;

  return isCitationList(citations) ? citations : undefined;
}

/** The result of running artefact detection on a message. */
type DetectionResult = ReturnType<typeof detectArtefacts>;

/** Add an artefact found in a streamed message to the artefact store. */
function addStreamedArtefact(artefact: Artefact): void {
  useArtefactStore.getState().addArtefact(artefact, { source: "sse-streaming" });
}

/**
 * Add the artefacts `extract` finds to the store, and to the detection result
 * so they are registered with the rest. Extraction runs only when called, so
 * its own logging keeps its place between the additions.
 */
function addSideChannelArtefacts(extract: () => Artefact[], detectionResult: DetectionResult) {
  extract().forEach((artefact) => {
    addStreamedArtefact(artefact);
    detectionResult.artefacts.push(artefact);
  });
}

/** Map a streamed message to its artefacts for navigation, and report detector warnings. */
function registerStreamedArtefactMapping(messageId: string, detectionResult: DetectionResult) {
  if (detectionResult.artefacts.length > 0) {
    const artefactIds = detectionResult.artefacts.map((a) => a.id);

    useArtefactStore.getState().registerMessageArtefacts(messageId, artefactIds);
  }

  if (detectionResult.warnings.length > 0) {
    logger.warn("Artefact detection warnings", {
      warnings: String(detectionResult.warnings),
    });
  }
}

/** Log how many artefacts a completed message held. */
function logDetectedArtefacts(
  messageId: string,
  messageLabel: string,
  detectionResult: DetectionResult,
) {
  logger.info(
    `🎨 Detected ${detectionResult.artefacts.length} artefacts in ${messageLabel} ${messageId}`,
    {
      artefactCount: String(detectionResult.artefacts.length),
      skipped: String(detectionResult.skippedCount),
    },
  );
}

/**
 * Detect artefacts in a completed message's content, add the graph and file
 * artefacts its tool executions carry (side-channel), and register them all
 * for navigation. `messageLabel` only words the log line.
 */
function registerCompletedMessageArtefacts(
  content: ChatMessage["content"],
  messageId: string,
  toolCallExecutions: ToolCallExecution[] | undefined,
  messageLabel: string,
): void {
  try {
    const detectionResult = detectArtefacts(String(content), messageId);

    detectionResult.artefacts.forEach((artefact) => addStreamedArtefact(artefact));

    // Extract graph and file artefacts from tool_call_executions (side-channel)
    addSideChannelArtefacts(
      () => extractGraphArtefactsFromToolExecs(toolCallExecutions || [], messageId),
      detectionResult,
    );
    addSideChannelArtefacts(
      () => extractFileArtefactsFromToolExecs(toolCallExecutions || [], messageId),
      detectionResult,
    );
    registerStreamedArtefactMapping(messageId, detectionResult);
    logDetectedArtefacts(messageId, messageLabel, detectionResult);
  } catch (error) {
    logger.error("Failed to detect artefacts", error);
  }
}

/** Store a message's citations. `messageLabel` only words the log line. */
function storeMessageCitations(
  messageId: string,
  citations: Citation[],
  messageLabel: string,
): void {
  try {
    useArtefactStore.getState().addCitations(messageId, citations);
    logger.info(`📝 Stored ${citations.length} citations for ${messageLabel} ${messageId}`);
  } catch (error) {
    logger.error("Failed to store citations", error);
  }
}

// ============================================================================
// Loading a chat from history
// ============================================================================

/** How long to wait for the admin API before falling back to localStorage. */
const CHAT_LOAD_TIMEOUT_MS = 10000;
const MS_PER_SECOND = 1000;

/**
 * Get chat history from the API with a timeout, falling back to the copy in
 * localStorage when the API fails. Throws when neither yields a chat.
 */
async function fetchChatHistoryWithFallback(
  adminClient: WeBuildAiAdmin,
  runId: string,
): Promise<LoadedChatHistory> {
  const chatStorageKey = `chat_${runId}`;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CHAT_LOAD_TIMEOUT_MS);

    const chatHistory = await Promise.race([
      adminClient.chatHistories.get({ id: runId }),
      new Promise<never>((_, reject) => {
        setTimeout(
          () =>
            reject(
              new Error(`Request timeout after ${CHAT_LOAD_TIMEOUT_MS / MS_PER_SECOND} seconds`),
            ),
          CHAT_LOAD_TIMEOUT_MS,
        );
      }),
    ]);

    clearTimeout(timeoutId);
    logger.info("✅ Chat history loaded from API", { chatId: String(chatHistory?.id) });

    return chatHistory;
  } catch (apiError) {
    logger.warn("⚠️ Failed to load from API, trying localStorage", {
      error: String(apiError),
    });

    // Fallback to localStorage
    const stored = localStorage.getItem(chatStorageKey);

    if (!stored) {
      throw apiError; // Re-throw original API error if no localStorage backup
    }

    try {
      const chatHistory = JSON.parse(stored);

      logger.info("✅ Chat history loaded from localStorage", {
        chatId: String(chatHistory?.id),
      });

      return chatHistory;
    } catch (parseError) {
      logger.error("❌ Failed to parse stored chat", parseError);
      throw new Error("Both API and localStorage failed", { cause: parseError });
    }
  }
}

/**
 * An attempt record as stored. The client type spells its fields in camelCase,
 * but the API may return the snake_case spellings read here.
 */
type StoredAttempt = AttemptData & { attempt_number?: number; variation_strategy?: string | null };

/**
 * A stored message as it may arrive: from the API in snake_case, or from
 * localStorage in camelCase. Every alias a loader reads is listed here. A
 * `ChatMessage` is assignable to it as it stands, so no conversion is needed.
 */
interface RawLoadedMessage {
  content?: ChatMessage["content"];
  files?: unknown;
  attachments?: unknown;
  attempt_history?: StoredAttempt[] | null;
  attemptHistory?: StoredAttempt[] | null;
  currentAttempt?: number | null;
  tool_call_executions?: ToolCallExecution[];
  toolcallexecutions?: ToolCallExecution[];
  tool_calls?: ToolCall[];
  toolcalls?: ToolCall[];
  message_type?: unknown;
  messageType?: unknown;
}

/** A file attached to a loaded message, with every field as text. */
type LoadedFile = { id: string; name: string; type: string; size: string };

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asText = (value: unknown): string => (typeof value === "string" ? value : String(value));

/** The spellings each field of a stored file may arrive under, in order of preference. */
const LOADED_FILE_ID_KEYS = ["id", "file_id", "uuid"] as const;
const LOADED_FILE_NAME_KEYS = ["name", "filename"] as const;
const LOADED_FILE_TYPE_KEYS = ["type", "mime_type", "file_type"] as const;
const LOADED_FILE_SIZE_KEYS = ["size", "filesize"] as const;
const DEFAULT_LOADED_FILE_TYPE = "application/octet-stream";

/**
 * The first of `keys` whose value is neither null nor undefined, or `fallback`.
 * This is the choice a chain of `??` makes, kept as a table so that each field's
 * spellings read as data.
 */
function firstPresent(
  record: Record<string, unknown>,
  keys: readonly string[],
  fallback?: unknown,
): unknown {
  for (const key of keys) {
    const value = record[key];

    if (value !== null && value !== undefined) return value;
  }

  return fallback;
}

/** Normalise a stored message's files from its files, attachments or content. */
function readLoadedFiles(raw: RawLoadedMessage): LoadedFile[] {
  const topLevelFiles = asArray(raw.files);
  const attachments = asArray(raw.attachments);
  const contentFiles = isRecord(raw.content) ? asArray(raw.content.files) : [];
  const rawFiles =
    topLevelFiles.length > 0 ? topLevelFiles : attachments.length > 0 ? attachments : contentFiles;

  // Normalise file fields. A file is kept when its raw id or name is truthy,
  // judged before the fields become text, so an id of 0 with no name is dropped.
  return rawFiles
    .filter(isRecord)
    .map((f) => ({
      id: firstPresent(f, LOADED_FILE_ID_KEYS) ?? String(f.name || f.filename || ""),
      name: firstPresent(f, LOADED_FILE_NAME_KEYS, ""),
      type: firstPresent(f, LOADED_FILE_TYPE_KEYS, DEFAULT_LOADED_FILE_TYPE),
      size: String(firstPresent(f, LOADED_FILE_SIZE_KEYS, "")),
    }))
    .filter((f) => f.id || f.name)
    .map((f) => ({ id: asText(f.id), name: asText(f.name), type: asText(f.type), size: f.size }));
}

/** Carry a message's files in its content, unless the content already embeds them. */
function attachFilesToContent(
  contentVal: ChatMessage["content"],
  normalisedFiles: LoadedFile[],
): ChatMessage["content"] {
  if (normalisedFiles.length === 0) return contentVal;

  if (typeof contentVal === "string") {
    return { prompt: contentVal, files: normalisedFiles };
  }

  if (contentVal && typeof contentVal === "object") {
    const embedded = Array.isArray(contentVal.files) ? contentVal.files : undefined;

    return embedded ? contentVal : { ...contentVal, files: normalisedFiles };
  }

  return contentVal;
}

/**
 * Check if main content is a system message and extract the current attempt's
 * content from attempt_history if so.
 */
function resolveSystemMessageContent(
  message: ChatMessage,
  contentVal: ChatMessage["content"],
  attemptHistory: StoredAttempt[],
  currentAttempt: number,
): ChatMessage["content"] {
  if (
    typeof contentVal !== "string" ||
    !(
      contentVal.includes("Direct call completed") ||
      contentVal.includes("response via agent reply mechanism")
    ) ||
    attemptHistory.length === 0
  ) {
    return contentVal;
  }

  // Find content for current attempt
  const currentAttemptData = attemptHistory.find(
    (attempt) => attempt.attempt_number === currentAttempt,
  );
  const resolvedContent = currentAttemptData?.content;

  if (!resolvedContent || typeof resolvedContent !== "string") return contentVal;

  logger.info(
    `🔄 ChatStore: Resolved system message to actual content for attempt ${currentAttempt}`,
    {
      messageId: String(message.id),
      systemMessage: contentVal.substring(0, 50) + "...",
      resolvedContentPreview: resolvedContent.substring(0, 100) + "...",
      strategy: String(currentAttemptData?.variation_strategy),
    },
  );

  return resolvedContent;
}

/** The message type of a stored message, from its snake_case or camelCase field. */
function readLoadedMessageType(raw: RawLoadedMessage): MessageType {
  return [raw.message_type, raw.messageType].find(isMessageType) ?? "assistant";
}

/** The attempt history of a stored message, under its camelCase or snake_case name. */
function readAttemptHistory(raw: RawLoadedMessage): StoredAttempt[] {
  // Check both camelCase and snake_case fields
  // Note: TypeScript client uses camelCase, but API might return snake_case
  return raw.attempt_history || raw.attemptHistory || [];
}

/** Log the raw attempt fields of a stored assistant message. */
function logRawAttemptData(
  message: ChatMessage,
  raw: RawLoadedMessage,
  attemptHistory: StoredAttempt[],
): void {
  if (message.role !== "assistant") return;

  logger.info("🔍 Raw message data for attempt extraction", {
    messageId: String(message.id),
    hasAttemptHistory: String(!!raw.attempt_history),
    hasAttemptHistoryCamel: String(!!raw.attemptHistory),
    attemptHistoryLength: String(attemptHistory.length),
  });
}

/** Log the attempt data a loaded message ended up with. */
function logAttemptProcessing(
  message: ChatMessage,
  messageType: MessageType,
  attempts: number,
  currentAttempt: number,
  attemptHistory: StoredAttempt[],
): void {
  logger.info("🔍 ChatStore: Processing message attempt data", {
    messageId: String(message.id),
    messageRole: String(message.role),
    messageType: String(messageType),
    finalAttempts: String(attempts),
    finalCurrentAttempt: String(currentAttempt),
    attemptHistoryCount: String(attemptHistory.length),
    shouldShow: String(attempts > 1),
  });
}

/**
 * Ensure a loaded message has the structure the UI expects: tool calls and
 * files under their camelCase names, attempt data, and the message type.
 */
function normaliseLoadedMessage(message: ChatMessage) {
  const raw: RawLoadedMessage = message;
  const toolCallExecutions =
    message.toolCallExecutions ||
    raw.tool_call_executions ||
    raw.toolcallexecutions ||
    EMPTY_TOOL_EXECUTIONS;
  const toolCalls = message.toolCalls || raw.tool_calls || raw.toolcalls || EMPTY_TOOL_CALLS;
  const normalizedFiles = readLoadedFiles(raw);
  const contentVal = attachFilesToContent(message.content, normalizedFiles);
  const attemptHistory = readAttemptHistory(raw);

  logRawAttemptData(message, raw, attemptHistory);

  const attempts = message.attempts ?? (attemptHistory.length > 0 ? attemptHistory.length : 1);
  const currentAttempt = message.currentAttempt || raw.currentAttempt || 1;
  const resolvedContent = resolveSystemMessageContent(
    message,
    contentVal,
    attemptHistory,
    currentAttempt,
  );
  // Extract messageType from various sources (snake_case from API, camelCase from localStorage)
  const messageType = readLoadedMessageType(raw);

  logAttemptProcessing(message, messageType, attempts, currentAttempt, attemptHistory);

  return {
    ...message,
    content: resolvedContent, // Use resolved content instead of original contentVal
    toolCallExecutions,
    toolCalls,
    messageType, // Preserve messageType for UI rendering
    ...(normalizedFiles.length > 0 ? { files: normalizedFiles } : {}),
    ...(attempts ? { attempts } : {}),
    ...(currentAttempt ? { currentAttempt } : {}),
    ...(attemptHistory.length > 0 ? { attempt_history: attemptHistory, attemptHistory } : {}),
  };
}

/** Map a loaded message's detected artefacts to it, and report detector warnings. */
function registerLoadedArtefactMapping(
  messageId: string,
  detectionResult: ReturnType<typeof detectArtefacts>,
): void {
  // Register message-to-artefact mapping for navigation
  if (detectionResult.artefacts.length > 0) {
    const artefactIds = detectionResult.artefacts.map((a) => a.id);

    useArtefactStore.getState().registerMessageArtefacts(messageId, artefactIds);
  }

  if (detectionResult.warnings.length > 0) {
    logger.warn(`Artefact detection warnings for message ${messageId}`, {
      warnings: String(detectionResult.warnings),
    });
  }
}

/**
 * Register the artefacts in a loaded assistant message: those detected in its
 * content and those its tool executions carry. Returns how many were added,
 * including any added before a failure, which is logged rather than thrown.
 */
function registerLoadedArtefacts(message: ChatMessage): number {
  // Only process assistant messages that have content
  if (message.role !== "assistant" || !message.content || typeof message.content !== "string") {
    return 0;
  }

  let added = 0;

  const addArtefact = (artefact: Artefact) => {
    useArtefactStore.getState().addArtefact(artefact, { source: "history-load" });
    added++;
  };

  try {
    const detectionResult = detectArtefacts(message.content, message.id);

    detectionResult.artefacts.forEach(addArtefact);

    const addSideChannelArtefact = (artefact: Artefact) => {
      addArtefact(artefact);
      detectionResult.artefacts.push(artefact);
    };

    // Extract graph and file artefacts from tool_call_executions (history-load)
    extractGraphArtefactsFromToolExecs(message.toolCallExecutions || [], message.id).forEach(
      addSideChannelArtefact,
    );
    extractFileArtefactsFromToolExecs(message.toolCallExecutions || [], message.id).forEach(
      addSideChannelArtefact,
    );
    registerLoadedArtefactMapping(message.id, detectionResult);
  } catch (error) {
    logger.warn(`Failed to detect artefacts for message ${message.id}`, {
      error: String(error),
    });
  }

  return added;
}

/** Register the citations in a loaded message. Returns how many it carries. */
function registerLoadedCitations(message: ChatMessage): number {
  if (!isCitationList(message.citations)) return 0;

  try {
    useArtefactStore.getState().addCitations(message.id, message.citations);
    logger.info(`📝 Found ${message.citations.length} citations in loaded message ${message.id}`);

    return message.citations.length;
  } catch (error) {
    logger.warn(`Failed to process citations for message ${message.id}`, {
      error: String(error),
    });

    return 0;
  }
}

/**
 * Register the artefacts and citations in a loaded message. Returns how many
 * of each were found.
 */
function registerLoadedMessage(message: ChatMessage): { artefacts: number; citations: number } {
  return {
    artefacts: registerLoadedArtefacts(message),
    citations: registerLoadedCitations(message),
  };
}

/** Process loaded messages for artefact detection and citations. */
function registerLoadedMessages(messages: ChatMessage[]): void {
  logger.info("🔍 Processing loaded messages for artefacts and citations...");
  let totalArtefactsFound = 0;
  let totalCitationsFound = 0;

  messages.forEach((message) => {
    const found = registerLoadedMessage(message);

    totalArtefactsFound += found.artefacts;
    totalCitationsFound += found.citations;
  });

  if (totalArtefactsFound > 0) {
    logger.info(`✅ Found ${totalArtefactsFound} artefacts in loaded chat history`);
  }
  if (totalCitationsFound > 0) {
    logger.info(`✅ Found ${totalCitationsFound} citations in loaded chat history`);
  }
}

/**
 * The assistant message a backend-flagged regeneration replaces: the last
 * message if it is the assistant's, otherwise the one before a trailing user
 * message. Only the backend's flag counts; in multi-agent workflows each agent
 * creates a NEW message, so a trailing assistant message alone is not enough.
 */
function findRegenerationTarget(messages: ChatMessage[], newMessageId: string): ChatMessage | null {
  const lastMessage = messages[messages.length - 1];

  if (lastMessage && lastMessage.role === "assistant") {
    logger.info("🔄 Backend flagged regeneration - replacing last assistant message", {
      lastMessageId: String(lastMessage?.id),
      newMessageId,
    });

    return lastMessage;
  }

  if (lastMessage && lastMessage.role === "user") {
    const prevMessage = messages[messages.length - 2];

    if (prevMessage && prevMessage.role === "assistant") {
      logger.info("🔄 Backend flagged regeneration - replacing previous assistant message", {
        lastMessageId: String(lastMessage?.id),
        targetMessageId: String(prevMessage.id),
      });

      return prevMessage;
    }
  }

  return null;
}

// ============================================================================
// Opening a run
// ============================================================================

/** A workflow config that names itself, which is what `create` requires. */
type IdentifiedWorkflowConfig = WorkflowConfig & { id: string; name: string };

/**
 * Reject a `create` call that lacks a platform client or workflow config, or
 * whose config has no id or name, logging what was missing first.
 */
function assertCreateArguments(
  platformClient: WebuildAiPlatform,
  workflowConfig: WorkflowConfig,
): asserts workflowConfig is IdentifiedWorkflowConfig {
  if (!platformClient || !workflowConfig) {
    logger.error("❌ Missing required parameters", undefined, {
      platformClient: String(!!platformClient),
      workflowConfig: String(!!workflowConfig),
    });
    throw new Error("Platform client or workflow config not found");
  }

  // Validate workflow structure before the platform sees it
  if (!workflowConfig.id || !workflowConfig.name) {
    logger.error("❌ Invalid workflow structure", undefined, {
      hasId: String(!!workflowConfig.id),
      hasName: String(!!workflowConfig.name),
      workflowConfig: JSON.stringify(workflowConfig),
    });
    throw new Error(
      `Invalid workflow structure - missing required fields: ${!workflowConfig.id ? "id " : ""}${!workflowConfig.name ? "name" : ""}`,
    );
  }
}

/** The model named by the first agent that names one, as text, or "" when none does. */
function findModelUsed(workflowConfig: WorkflowConfig): string {
  let modelUsed = "";

  if (workflowConfig.agents) {
    const agentWithModel = Object.values(workflowConfig.agents).find(
      (agent) => agent.parameters?.model,
    );

    if (agentWithModel && agentWithModel.parameters?.model) {
      modelUsed = String(agentWithModel.parameters.model);
    }
  }

  return modelUsed;
}

/**
 * A copy of the config with `system_message` stripped from every agent's
 * parameters, to avoid exposing it in browser network requests. The platform
 * backend fetches it server-side from the admin API.
 */
function withoutSystemMessages(workflowConfig: WorkflowConfig): WorkflowConfig {
  const sanitizedAgents: typeof workflowConfig.agents = {};

  for (const [id, agent] of Object.entries(workflowConfig.agents || {})) {
    const { system_message: _, systemMessage: __, ...restParams } = agent.parameters || {};

    sanitizedAgents[id] = { ...agent, parameters: restParams };
  }

  return { ...workflowConfig, agents: sanitizedAgents };
}

/** The sidebar row for a chat the platform has opened but not yet persisted. */
function optimisticHistoryRow(chatObj: ChatHistory, workflowType: string): ChatHistoryMetadata {
  return {
    id: chatObj.id,
    tenant: chatObj.tenant,
    username: chatObj.username,
    fileLocation: "", // Will be set by backend when persisted
    summary: chatObj.summary,
    createdAt: chatObj.timestamp,
    updatedAt: chatObj.timestamp,
    messageCount: 0,
    status: "streaming",
    workflowType,
    metrics: chatObj.metrics,
  };
}

// ============================================================================
// Sending a message
// ============================================================================

/** The prefix pydantic gives a validator's message, which means nothing to the reader. */
const VALUE_ERROR_PREFIX = /^Value error, /i;

/**
 * The messages a new message is sent after. When the last assistant message
 * was stopped, it goes, together with the user message that prompted it and
 * everything after that, so a stopped exchange disappears once the person
 * sends something new.
 */
function withoutStoppedExchange(messages: ChatMessage[]): ChatMessage[] {
  let messagesToUse = messages;
  const lastAssistantMsg = [...messages]
    .reverse()
    .find((msg: ChatMessage) => msg.role === "assistant");

  if (lastAssistantMsg && lastAssistantMsg.status === "stopped") {
    // Find the user message that triggered the stopped response
    const lastAssistantIndex = messages.findIndex(
      (msg: ChatMessage) => msg.id === lastAssistantMsg.id,
    );

    if (lastAssistantIndex > 0) {
      // Look for the user message just before the stopped assistant message
      let userMsgIndex = lastAssistantIndex - 1;

      while (userMsgIndex >= 0 && messages[userMsgIndex].role !== "user") {
        userMsgIndex--;
      }

      if (userMsgIndex >= 0) {
        // Remove from the user message onwards (user msg + stopped assistant response)
        messagesToUse = messages.slice(0, userMsgIndex);
      }
    }
  }

  return messagesToUse;
}

/**
 * The message to show when the platform rejects an invoke. A validation error
 * (422) carries messages the backend wrote for end users, so those are shown;
 * anything else gets the generic message.
 */
function invokeFailureMessage(invokeError: unknown): string {
  let errorMessage = "An error occurred while sending your message. Please try again.";

  if (invokeError instanceof HTTPValidationError && invokeError.detail?.length) {
    // Join all validation error messages (there may be more than one).
    // These are explicitly written by the backend for end-users.
    errorMessage = invokeError.detail.map((e) => e.msg.replace(VALUE_ERROR_PREFIX, "")).join(". ");
  }

  return errorMessage;
}

/** What a failed invoke rolls back and reports, and the chat it started from. */
interface InvokeFailure {
  /** The chat as it is now, which may have moved on while the invoke was in flight. */
  currentChat: ChatHistory | null;
  /** The chat the invoke started from, used only when there is no current chat. */
  startingChat: ChatHistory;
  /** The messages the person's message was appended to. */
  messagesToUse: ChatMessage[];
  userMsgId: string;
  errorMessage: string;
}

/** The chat with the person's unsent message rolled back and the error shown in its place. */
function chatWithInvokeError(failure: InvokeFailure): ChatHistory {
  const { currentChat, startingChat, messagesToUse, userMsgId, errorMessage } = failure;
  const rolledBackMessages = currentChat
    ? currentChat.messages.filter((m) => m.id !== userMsgId)
    : [...messagesToUse];

  const errorMsg: ChatMessage = {
    id: String(Date.now()),
    role: "assistant",
    content: errorMessage,
    status: "failed",
    liked: null,
    timestamp: new Date().toISOString(),
  };

  return {
    ...(currentChat || startingChat),
    messages: [...rolledBackMessages, errorMsg],
  };
}

/** Tear down a run's SSE connection, if one is open. */
function closeActiveConnection(runId: string): void {
  if ((window as any).__activeSSEConnections?.[runId]) {
    (window as any).__activeSSEConnections[runId].cleanup();
  }
}

/**
 * Stable, locale-independent upload status. Kept as a status key (not a
 * translated string) so logic can compare it reliably; translate only at
 * display time via fileUploadStatusLabel (see utils/chat/fileOperations).
 */
export type FileUploadStatus = "uploading" | "processing" | "completed";

export interface UploadedFile {
  name: string;
  size: string;
  type: string;
  id: string;
  /** Stable status key for upload progress (see FileUploadStatus). */
  status?: FileUploadStatus;
}

export interface InputRequest {
  messageId: string;
  question: string;
  agent: string;
  timestamp: string;
}

/**
 * How a caller of `create` describes the run it is opening.
 *
 * An options object rather than a sixth positional argument, because a bare
 * `false` in sixth place says nothing at the call site about what it turns off.
 */
export interface CreateRunOptions {
  /**
   * Whether the run belongs in the person's chat history sidebar. The test is
   * whether anything would want to reopen the run, rather than whether it is
   * technically a conversation. Leave it undeclared for one, which is what the
   * chat page and the composer's file upload do, and pass `false` for a run
   * with nothing to reopen, such as the Workflow Builder's generator session.
   *
   * A run that is not a conversation can still need its row. The Automations
   * "Run now" firing declares nothing for exactly that reason, the row being
   * the only route that page has to what a firing produced, and ADR-0027
   * records why.
   *
   * An undeclared flag means visible, both here and on the platform, so an
   * omission can only ever fail towards the behaviour that existed before the
   * flag did.
   *
   * Only an explicit `false` does anything. `true` is accepted so the field can
   * be spelled out where a caller wants to say what it is, but it is treated
   * exactly as an omission and is not sent, so a conversation's request carries
   * no `visible` key either way. Do not read a missing key on the wire as the
   * caller having failed to declare itself.
   */
  visible?: boolean;
}

export interface ChatStoreState {
  streaming: boolean;
  chatLoading: boolean;
  chatWorkflowId: string | null;
  runId: string | null;
  chat: ChatHistory | null;
  tokenUsage: TokenUsage | null;
  pendingInputRequest: InputRequest | null;

  create: (
    platformClient: WebuildAiPlatform,
    workflowConfig: WorkflowConfig,
    username: string,
    tenant: string,
    summary: string,
    options?: CreateRunOptions,
  ) => Promise<string>;
  invoke: (
    adminClient: WeBuildAiAdmin,
    platformClient: WebuildAiPlatform,
    data: InvokeMessage,
  ) => Promise<void>;
  subscribe: (
    sseWorkflowClient: (workflowId: string, lastEventId?: string) => EventSource | null,
    adminClient: WeBuildAiAdmin,
    userId: string,
    fetchChatHistory?: (userId: string, adminClient: WeBuildAiAdmin) => Promise<void>,
  ) => void;
  load: (adminClient: WeBuildAiAdmin, runId: string) => Promise<void>;
  submitFeedback: (
    adminClient: WeBuildAiAdmin,
    runId: string,
    messageId: string,
    liked: boolean,
    feedback: string,
  ) => Promise<void>;
  updateMessageFeedback: (messageId: string, liked: boolean | null) => void;
  updateMessageComment: (messageId: string, feedback: string) => void;
  updateMessage: (chatId: string, messageId: string, updates: Partial<ChatMessage>) => void;
  stopStreaming: (
    adminClient: WeBuildAiAdmin,
    platformClient?: WebuildAiPlatform,
    discardMessages?: boolean,
  ) => Promise<void>;
  save: (adminClient: WeBuildAiAdmin, chatHistory: ChatHistory) => Promise<void>;
  newChat: () => void;
  setCurrentWorkflowId: (workflowId: string) => void;
  setOnChatSaved: (callback: ((chatId: string, workflowId: string) => void) | undefined) => void;
}

const useChatStore = create<
  ChatStoreState & {
    resetPrompt: boolean;
    triggerResetPrompt: () => void;
    clearResetPrompt: () => void;
    onChatSaved: ((chatId: string, workflowId: string) => void) | undefined;
  }
>((set, get) => {
  // Note: Zustand's set() should NEVER throw errors - it's synchronous and safe.
  // If we're seeing lifecycle errors, they're coming from React components,
  // not from the store itself. We should NOT trap them here.
  // Using direct set() and letting any real errors propagate.

  return {
    streaming: false,
    chatLoading: false,
    chatWorkflowId: null,
    runId: null,
    chat: null,
    tokenUsage: null,
    pendingInputRequest: null,

    resetPrompt: false,
    triggerResetPrompt: () => set({ resetPrompt: true }),
    clearResetPrompt: () => set({ resetPrompt: false }),

    // Chat saved callback for URL state synchronization
    onChatSaved: undefined as ((chatId: string, workflowId: string) => void) | undefined,

    newChat: () => {
      logger.info("🆕 ChatStore.newChat: Starting new chat, clearing selection");

      // Clear the chat history selection FIRST before anything else
      useChatHistoryStore.getState().clearSelection();

      // Clean up any active SSE connections before starting new chat
      const currentRunId = get().runId;

      if (currentRunId) {
        const activeConnections = (window as any).__activeSSEConnections || {};
        const connection = activeConnections[currentRunId];

        if (connection) {
          logger.info("🛑 Cleaning up SSE connection for new chat", {
            runId: String(currentRunId),
          });

          // Abort the connection using AbortController
          if (connection.abortController) {
            connection.abortController.abort();
          }

          // Call cleanup function
          if (typeof connection.cleanup === "function") {
            connection.cleanup();
          }

          // Remove from registry
          delete activeConnections[currentRunId];
        }
      }

      // Clear any workflow-specific localStorage that might persist state
      try {
        const keysToRemove = [];

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);

          if (key && key.startsWith("chat_")) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));

        if (keysToRemove.length > 0) {
          logger.info("🧹 ChatStore.newChat: Cleared chat-related localStorage keys", {
            keys: String(keysToRemove),
          });
        }
      } catch (error) {
        logger.warn("Failed to clear chat-related localStorage in newChat", {
          error: String(error),
        });
      }

      set({
        runId: null,
        chat: null,
        chatWorkflowId: null,
        streaming: false,
        pendingInputRequest: null,
      });

      // Clear artefacts and citations from ArtefactStore
      useArtefactStore.getState().clearArtefacts();
      useArtefactStore.getState().clearCitations();
    },

    setCurrentWorkflowId: (workflowId: string) => {
      set({ chatWorkflowId: workflowId });
    },

    setOnChatSaved: (callback: ((chatId: string, workflowId: string) => void) | undefined) => {
      set({ onChatSaved: callback });
    },

    save: async (adminClient: WeBuildAiAdmin, chatHistory: ChatHistory) => {
      if (!adminClient || !chatHistory) return;

      try {
        logger.info("💾 Saving chat...");

        const currentChat = get().chat;

        // Only update state if the chat has actually changed
        if (currentChat?.id === chatHistory.id) {
          // Check if there are meaningful differences before updating state
          const currentMessagesLength = currentChat.messages?.length || 0;
          const newMessagesLength = chatHistory.messages?.length || 0;
          const lastCurrentMessage =
            currentMessagesLength > 0 ? currentChat.messages[currentMessagesLength - 1] : null;
          const lastNewMessage =
            newMessagesLength > 0 ? chatHistory.messages[newMessagesLength - 1] : null;

          // Only update if message count changed or last message content/status changed
          const shouldUpdate =
            currentMessagesLength !== newMessagesLength ||
            lastCurrentMessage?.content !== lastNewMessage?.content ||
            lastCurrentMessage?.status !== lastNewMessage?.status;

          if (shouldUpdate) {
            set({ chat: chatHistory });
          }
        } else if (!currentChat) {
          // Set initial chat if none exists
          set({ chat: chatHistory });
        }

        // Store in localStorage as backup
        const chatStorageKey = `chat_${chatHistory.id}`;
        const updatedChat = get().chat;

        if (updatedChat?.id === chatHistory.id) {
          localStorage.setItem(chatStorageKey, JSON.stringify(updatedChat));
          logger.info(`💾 Chat ${chatHistory.id} backed up to localStorage`);
        }

        logger.info("✅ Chat save completed");

        // Trigger URL state callback if registered
        const { onChatSaved, chatWorkflowId } = get();

        if (onChatSaved && chatHistory.id && chatWorkflowId) {
          logger.info("🔗 Triggering URL update callback", {
            chatId: String(chatHistory.id),
            workflowId: String(chatWorkflowId),
          });
          onChatSaved(chatHistory.id, chatWorkflowId);
        }
      } catch (error) {
        logger.error("❌ Error in save method", error);
      }
    },

    stopStreaming: async (
      adminClient: WeBuildAiAdmin,
      platformClient?: WebuildAiPlatform,
      discardMessages: boolean = true,
    ) => {
      const currentChat = get().chat;

      if (!currentChat) return;

      const runId = get().runId;

      // First, call the backend stop endpoint to cancel server-side execution
      // This ensures the workflow stops processing and messages are properly handled
      if (runId && platformClient) {
        try {
          // Use the SDK method for stopping workflows
          await platformClient.workflows.stopStreaming({
            workflowId: String(runId),
            discardMessages: discardMessages,
          });

          // Mark the message as stopped but keep it visible
          // Messages will be removed when user sends a new message (handled in invoke)
        } catch (error) {
          logger.error("Error calling backend stop endpoint", { error: String(error) });
          // Continue with client-side cleanup even if backend call fails
        }
      }

      // Update local state to reflect stopped status - keep message visible
      // The stopped message will be removed when user sends a new message (handled in invoke)
      const lastMessage = currentChat.messages
        .slice()
        .reverse()
        .find((msg: ChatMessage) => msg.role === "assistant" && msg.status === "streaming");

      if (lastMessage) {
        const updatedMessages = currentChat.messages.map((msg: ChatMessage) =>
          msg.id === lastMessage.id ? { ...msg, status: "stopped" as const } : msg,
        );
        const updatedChat = { ...currentChat, messages: updatedMessages };

        set({ chat: updatedChat });
        await get().save(adminClient, updatedChat);
      }

      set({ streaming: false });

      // Close the SSE connection if it exists
      if (runId && (window as any).__activeSSEConnections?.[runId]) {
        logger.info("🛑 Stopping SSE connection for runId", { runId: String(runId) });
        (window as any).__activeSSEConnections[runId].cleanup();
      }
    },

    /**
     * Opens a platform run and adopts it as the store's current chat.
     *
     * `options.visible` is the caller's statement about what kind of run this
     * is, and it governs two things that have to move together. It is sent to
     * the platform, which stamps it onto the run's metadata row so the admin
     * API can leave the run out of the chat history listing and its search. And
     * it decides whether the optimistic sidebar row below is written at all.
     *
     * Skipping that row for a hidden run is not cosmetic. A hidden run never
     * comes back from `listChatHistories`, and `mergeChatHistories` keeps a
     * local-only entry rather than dropping it, so an optimistic row for one
     * would survive every refresh as a phantom nothing could clear. The
     * Builder's generator session is the worst case, since it does not pass
     * `fetchChatHistory` to `subscribe` and so never reconciles at all.
     *
     * The flag is sent only when a caller declares one, so a conversation's
     * request is byte-identical to what it was before the flag existed.
     */
    create: async (platformClient, workflowConfig, username, tenant, summary, options) => {
      // Undeclared means visible, matching the platform's own reading of an
      // absent flag, so only an explicit `false` hides a run.
      const isHidden = options?.visible === false;

      logger.info("🚀 ChatStore.create called", {
        hasPlatformClient: String(!!platformClient),
        hasWorkflowConfig: String(!!workflowConfig),
        workflowId: String(workflowConfig?.id),
        workflowName: String(workflowConfig?.name),
        username,
        tenant,
        summary,
        hidden: String(isHidden),
      });

      assertCreateArguments(platformClient, workflowConfig);

      if (get().streaming) {
        throw new Error("Streaming is already in progress");
      }

      const modelUsed = findModelUsed(workflowConfig);

      logger.info("🌐 Calling platformClient.workflows.create", {
        workflowConfigKeys: String(Object.keys(workflowConfig || {})),
      });

      const sanitizedConfig = withoutSystemMessages(workflowConfig);

      const response = await platformClient.workflows.create({
        createWorkflowReq: {
          workflow: sanitizedConfig,
          // Spread rather than always sending the flag, so a conversation's
          // request carries no `visible` key at all and stays exactly what it
          // was before this existed.
          ...(isHidden ? { visible: false } : {}),
        },
      });

      logger.info("✅ Workflow creation successful", {
        workflowId: String(response.workflowId),
        hasResponse: String(!!response),
      });
      const chatObj: ChatHistory = {
        id: response.workflowId,
        tenant,
        username,
        timestamp: new Date().toISOString(),
        modelUsed,
        messages: [],
        metrics: {
          usage: {},
        },
        summary,
      };

      set({
        runId: response.workflowId,
        chat: chatObj,
        chatWorkflowId: workflowConfig.id,
      });

      // Add the new chat to the history sidebar immediately, but only for a run
      // the listing will eventually return. See this action's docstring for why
      // an optimistic row for a hidden run would never clear.
      if (!isHidden) {
        // Convert ChatHistory to ChatHistoryMetadata for the sidebar
        const chatMetadata = optimisticHistoryRow(chatObj, workflowConfig.id);

        useChatHistoryStore.getState().addNewChat(chatMetadata);
      }

      logger.info("💭 Chat created in memory, waiting for platform to persist to backend...");

      return response.workflowId;
    },

    invoke: async (adminClient, platformClient, data) => {
      const { runId, chat, pendingInputRequest } = get();

      if (!runId || !chat || !platformClient || !adminClient) {
        throw new Error("Missing required dependencies for chat invocation");
      }
      if (get().streaming) {
        return;
      }
      set({ streaming: true });

      // Check if the last assistant message is "stopped" - if so, remove it and its user message
      // This ensures stopped messages disappear when user sends a new message
      const messagesToUse = withoutStoppedExchange(chat.messages);

      const userMsg: ChatMessage = {
        id: String(messagesToUse.length + 1),
        role: "user",
        // Store full message data including mentions for display purposes
        // data may include extended mention fields not in the base ChatMessage content type
        content: data as unknown as ChatMessage["content"],
        status: "success",
        timestamp: new Date().toISOString(),
      };
      const updatedChat = { ...chat, messages: [...messagesToUse, userMsg] };

      set({ chat: updatedChat });
      await get().save(adminClient, updatedChat);

      // Check if this is a response to a human-in-the-loop input request
      if (pendingInputRequest) {
        logger.info("📤 Submitting user input for human-in-the-loop (via invoke endpoint)", {
          runId: String(runId),
          messageId: pendingInputRequest.messageId,
        });

        // Clear the pending input request BEFORE invoking
        // The backend will detect workflow.waitingUserInput and route correctly
        set({ pendingInputRequest: null });
      }

      // Always use the normal invoke endpoint
      // The backend detects if workflow is waiting for input and routes accordingly
      logger.info("🚀 Invoking workflow", {
        runId: String(runId),
        runIdType: typeof runId,
        isInputResponse: String(!!pendingInputRequest),
      });

      // SDK 0.11.0 original structure
      // Type assertion needed because SDK type doesn't include our extended mention fields

      try {
        await platformClient.workflows.invoke({
          workflowId: String(runId),
          // SDK type does not include extended mention fields — cast via unknown
          invokeWorkflowReq: {
            data: data as unknown as Record<
              string,
              string | string[] | Record<string, string> | Record<string, string>[]
            >,
          },
        });

        logger.info("✅ Workflow invoke successful");
      } catch (invokeError) {
        // Extract a user-friendly message from validation errors (422)
        const errorMessage = invokeFailureMessage(invokeError);

        logger.error("❌ Workflow invoke failed", invokeError, {
          runId: String(runId),
          errorMessage,
        });

        // Roll back the optimistically-added user message and show the error
        // instead.  The backend never received this message (the HTTP call
        // failed), so keeping it in state would cause a mismatch: the
        // messages appear in the UI but vanish when the chat is reloaded
        // from the API.
        // Use get().chat (not the stale `chat` closure) to avoid overwriting
        // concurrent state mutations (e.g. SSE events from a prior workflow).
        const chatWithError = chatWithInvokeError({
          currentChat: get().chat,
          startingChat: chat,
          messagesToUse,
          userMsgId: userMsg.id,
          errorMessage,
        });

        // Tear down the SSE connection *before* writing error state so an
        // in-flight SSE event cannot race and overwrite the error message.
        closeActiveConnection(runId);

        set({ chat: chatWithError, streaming: false });
        // Defer save — read fresh state to avoid overwriting rapid follow-up mutations
        setTimeout(() => {
          const latestChat = get().chat;

          if (latestChat) {
            get().save(adminClient, latestChat);
          }
        }, 0);

        // Don't re-throw — the error is already surfaced in the chat UI
        return;
      }
    },

    subscribe: (sseWorkflowClient, adminClient, userId, fetchChatHistory) => {
      const { runId } = get();

      if (!runId || !sseWorkflowClient || !adminClient) {
        throw new Error("Missing required dependencies");
      }

      logger.info("🔌 Subscribe called for runId", { runId: String(runId) });

      // Initialize global SSE connection tracking if it doesn't exist
      if (!(window as any).__activeSSEConnections) {
        (window as any).__activeSSEConnections = {};
      }

      // Check if there's already an active SSE connection for this runId
      const existingConnection = (window as any).__activeSSEConnections[runId];

      if (existingConnection) {
        logger.info("🔌 SSE connection already active for runId", { runId: String(runId) });

        return existingConnection.cleanup;
      }

      // --- SSE Reconnection State (ADR-0014) ---
      // Mutable ref so event handlers always use the current EventSource instance
      let currentSe: EventSource | null = sseWorkflowClient(runId);

      if (!currentSe) return;

      // Track the last SSE event ID for reconnection replay (monotonically incrementing integer)
      let lastEventId: string | null = null;
      // Track when we last received any event (including heartbeats) for dead-connection detection
      let lastEventTimestamp = Date.now();
      // Heartbeat check interval — polls every 10s, triggers reconnect after 60s of silence
      let heartbeatCheckInterval: ReturnType<typeof setInterval> | null = null;
      // Track the highest processed event ID to skip duplicates on replay
      let lastProcessedEventId = 0;
      // Reconnect attempt counter for logging
      let reconnectCount = 0;
      // Flag to skip gap detection on first event after reconnect (heartbeat IDs
      // increment the backend counter, creating expected gaps that aren't data loss)
      let skipNextGapCheck = false;
      // Maximum reconnect attempts before giving up
      const MAX_RECONNECT_ATTEMPTS = 10;
      // Heartbeat timeout threshold (2 missed heartbeats at 30s interval)
      const HEARTBEAT_TIMEOUT_MS = 60_000;
      // Heartbeat check polling interval
      const HEARTBEAT_CHECK_INTERVAL_MS = 10_000;
      // Timeout for REST API fallback when recovering from replay buffer overflow
      const API_REFRESH_TIMEOUT_MS = 10_000;

      // Track if this SSE connection is still active
      let cleanupTimeoutId: ReturnType<typeof setTimeout> | null = null;

      // Create AbortController for proper cancellation - single source of truth
      const abortController = new AbortController();
      const { signal } = abortController;

      /**
       * Register all SSE event listeners on an EventSource instance.
       * Called on initial connection and after each reconnection.
       */
      const registerEventListeners = (se: EventSource) => {
        se.addEventListener(SSEEventType.MESSAGE, handleOutput);
        se.addEventListener(SSEEventType.OUTPUT, handleOutput);
        se.addEventListener(SSEEventType.REGENERATION, handleOutput);
        se.addEventListener(SSEEventType.REGENERATION_STARTED, handleRegenerationStarted);
        se.addEventListener(SSEEventType.WORKFLOW_COMPLETED, handleWorkflowCompletion);
        se.addEventListener(SSEEventType.MESSAGE_COMPLETED, handleMessageCompletion);
        se.addEventListener(SSEEventType.REGENERATION_COMPLETED, handleMessageCompletion);
        se.addEventListener(SSEEventType.ERROR, handleError);
        se.addEventListener(SSEEventType.INPUT, handleInputRequest);
        // Heartbeat listener — resets the dead-connection timer (ADR-0014)
        se.addEventListener(SSEEventType.HEARTBEAT, handleHeartbeat);
      };

      /**
       * Heartbeat handler — reset the liveness timer on each heartbeat.
       */
      const handleHeartbeat = (event: MessageEvent) => {
        if (signal.aborted) return;
        lastEventTimestamp = Date.now();
        // Track the event ID from heartbeats too
        if (event.lastEventId) {
          lastEventId = event.lastEventId;
        }
      };

      /**
       * Update liveness timestamp and event ID tracking on any received event.
       * Called at the top of every event handler.
       */
      const trackEvent = (event: MessageEvent) => {
        lastEventTimestamp = Date.now();
        if (event.lastEventId) {
          lastEventId = event.lastEventId;
        }
      };

      // Whether a REST API refresh is in progress (prevents duplicate fetches)
      let isRefreshingFromAPI = false;

      /**
       * Process an incoming event's ID for deduplication and gap detection.
       * Returns true if the event should be skipped (already processed).
       *
       * Side effects:
       * - Updates `lastProcessedEventId` to track highest seen ID
       * - Detects gaps in event IDs after reconnect (buffer overflow)
       *   and triggers REST API fallback to recover missing content
       *
       * Note: gap detection is skipped for the first event after reconnect
       * because heartbeat IDs increment the backend counter during disconnect,
       * creating expected gaps that don't represent data loss.
       */
      const processEventIdAndCheckDuplicate = (event: MessageEvent): boolean => {
        if (!event.lastEventId) return false;
        const eventId = parseInt(event.lastEventId, 10);

        if (isNaN(eventId)) return false;

        if (eventId <= lastProcessedEventId) {
          logger.info("🔁 Skipping duplicate event after reconnect", {
            eventId: String(eventId),
            lastProcessed: String(lastProcessedEventId),
          });

          return true;
        }

        // Detect gap: if the incoming event ID is more than 1 ahead of what we last processed,
        // events were lost from the replay buffer (buffer overflow during disconnect).
        // Skip gap check on first event after reconnect — heartbeat IDs increment the
        // backend counter during disconnect, so a gap is expected and doesn't mean data loss.
        const gap = eventId - lastProcessedEventId;

        if (gap > 1 && reconnectCount > 0 && !skipNextGapCheck && !isRefreshingFromAPI) {
          logger.warn("⚠️ Gap detected in SSE event IDs after reconnect — fetching full state", {
            lastProcessed: String(lastProcessedEventId),
            receivedId: String(eventId),
            missedEvents: String(gap - 1),
          });
          refreshChatFromAPI();
        }

        // Reset the skip flag after first event post-reconnect
        if (skipNextGapCheck) {
          skipNextGapCheck = false;
        }

        // Update the highest processed event ID
        lastProcessedEventId = eventId;

        return false;
      };

      /**
       * Fetch the full chat state from REST API to recover content lost
       * during a reconnection gap (when replay buffer overflowed).
       *
       * Note: The REST API only returns completed messages — the currently
       * streaming message is not persisted until message_completed. The user
       * may see a gap in the in-progress response, but message_completed
       * delivers the full final text which replaces the partial content.
       *
       * To avoid a visible flash where partial streaming content disappears,
       * the last in-progress message is preserved and appended after the
       * API messages.
       */
      const refreshChatFromAPI = async () => {
        if (isRefreshingFromAPI || signal.aborted) return;
        isRefreshingFromAPI = true;

        try {
          logger.info("🔄 Fetching full chat state from API after reconnect gap", {
            runId: String(runId),
          });

          const chatHistory = await Promise.race([
            adminClient.chatHistories.get({ id: runId }),
            new Promise<never>((_, reject) => {
              setTimeout(() => reject(new Error("Chat refresh timeout")), API_REFRESH_TIMEOUT_MS);
            }),
          ]);

          if (signal.aborted) return;

          if (chatHistory && chatHistory.messages) {
            const chat = get().chat;

            if (chat) {
              const apiMessages = chatHistory.messages as ChatMessage[];

              // Preserve the last in-progress streaming message to avoid a visible flash
              // where partial content disappears then reappears when message_completed arrives
              const lastMsg = chat.messages[chat.messages.length - 1];
              const isStreaming =
                lastMsg && lastMsg.role === "assistant" && lastMsg.status === "streaming";
              const merged = isStreaming ? [...apiMessages, lastMsg] : apiMessages;

              set({ chat: { ...chat, messages: merged } });
              logger.info("✅ Chat state recovered from API after reconnect gap", {
                messageCount: String(merged.length),
                preservedStreamingMsg: String(!!isStreaming),
              });
            }
          }
        } catch (error) {
          logger.warn("⚠️ Failed to refresh chat from API after reconnect gap", {
            error: String(error),
          });
          // Non-fatal: streaming continues, user just has a gap in content
        } finally {
          isRefreshingFromAPI = false;
        }
      };

      /**
       * Force reconnect after detecting a dead connection (ADR-0014).
       * Closes the current EventSource, creates a new one with Last-Event-ID,
       * and re-registers all event listeners.
       */
      const forceReconnect = () => {
        if (signal.aborted) return;

        reconnectCount++;

        if (reconnectCount > MAX_RECONNECT_ATTEMPTS) {
          logger.error("🔌 Max SSE reconnect attempts reached, giving up", {
            runId: String(runId),
            attempts: String(reconnectCount),
          });
          // Show error to user and stop streaming
          const chat = get().chat;

          if (chat && !signal.aborted) {
            const errorMsg: ChatMessage = {
              id: String(Date.now()),
              role: "assistant",
              content: "Connection lost after multiple reconnection attempts. Please try again.",
              status: "failed",
              liked: null,
              timestamp: new Date().toISOString(),
            };

            set({
              chat: { ...chat, messages: [...chat.messages, errorMsg] },
              streaming: false,
            });
          }
          cleanup();

          return;
        }

        logger.info("🔄 SSE reconnecting (dead connection detected)", {
          runId: String(runId),
          attempt: String(reconnectCount),
          lastEventId: lastEventId ?? "none",
          silentSeconds: String(Math.round((Date.now() - lastEventTimestamp) / 1000)),
        });

        // Close the hung EventSource
        if (currentSe) {
          currentSe.close();
        }

        // Create a new EventSource with Last-Event-ID for replay.
        // Backend confirmed: "0" triggers replay from beginning of history
        // (int("0") = 0, backend event IDs start at 1, so event.id > 0 matches all).
        // sseWorkflowClient is safe to capture in closures — getAccessTokenSilently()
        // is called per-request inside the fetch override, so reconnections always use a fresh token.
        const reconnectId = lastEventId ?? "0";
        const newSe = sseWorkflowClient(runId, reconnectId);

        if (!newSe) {
          logger.error("🔌 Failed to create new EventSource on reconnect", {
            runId: String(runId),
          });
          cleanup();

          return;
        }

        // Swap the current EventSource reference and re-register listeners
        currentSe = newSe;
        registerEventListeners(newSe);

        // Skip gap detection on the first event after reconnect — heartbeat IDs
        // increment the backend counter during disconnect, creating expected gaps
        skipNextGapCheck = true;

        // Reset liveness timestamp
        lastEventTimestamp = Date.now();

        logger.info("✅ SSE reconnected successfully", {
          runId: String(runId),
          attempt: String(reconnectCount),
          lastEventId: reconnectId,
        });
      };

      /**
       * Start the heartbeat check interval.
       * Polls every 10s and triggers reconnect if no event in 60s.
       */
      const startHeartbeatCheck = () => {
        if (heartbeatCheckInterval) {
          clearInterval(heartbeatCheckInterval);
        }
        heartbeatCheckInterval = setInterval(() => {
          if (signal.aborted) {
            if (heartbeatCheckInterval) {
              clearInterval(heartbeatCheckInterval);
              heartbeatCheckInterval = null;
            }

            return;
          }

          const silentMs = Date.now() - lastEventTimestamp;

          if (silentMs > HEARTBEAT_TIMEOUT_MS) {
            logger.warn("💀 SSE heartbeat timeout — no event received", {
              runId: String(runId),
              silentSeconds: String(Math.round(silentMs / 1000)),
            });
            forceReconnect();
          }
        }, HEARTBEAT_CHECK_INTERVAL_MS);
      };

      // Start monitoring for dead connections
      startHeartbeatCheck();

      const cleanup = () => {
        // Performance telemetry - start cleanup measurement
        const cleanupStart = performance.now();

        // Abort FIRST - this makes signal.aborted true atomically
        abortController.abort();

        if (currentSe) {
          currentSe.close();
        }
        // Clear heartbeat check interval (ADR-0014)
        if (heartbeatCheckInterval) {
          clearInterval(heartbeatCheckInterval);
          heartbeatCheckInterval = null;
        }
        // Clear any pending cleanup timeout
        if (cleanupTimeoutId) {
          clearTimeout(cleanupTimeoutId);
          cleanupTimeoutId = null;
        }
        // Remove from global tracking
        if ((window as any).__activeSSEConnections) {
          delete (window as any).__activeSSEConnections[runId];
        }
        logger.info("🔌 SSE connection cleaned up for runId", { runId: String(runId) });

        // Performance telemetry - end cleanup measurement
        performance.measure("sse-cleanup", { start: cleanupStart, end: performance.now() });

        // Log performance metrics in development mode
        if (process.env.NODE_ENV === "development") {
          // Delay slightly to ensure all measurements are captured
          setTimeout(() => logSSEPerformanceMetrics(), 100);
        }
      };

      // Register this connection globally with cleanup timeout cancellation and abort controller
      (window as any).__activeSSEConnections[runId] = {
        cleanup,
        isActive: () => !signal.aborted, // Use abort signal as single source of truth
        abortController, // Expose for external cancellation
        signal, // Expose signal for checking if aborted
        clearCleanupTimeout: () => {
          if (cleanupTimeoutId) {
            clearTimeout(cleanupTimeoutId);
            cleanupTimeoutId = null;
            logger.info("⏸️ Cancelled delayed cleanup for runId", { runId: String(runId) });
          }
        },
      };

      /**
       * Store the messages after a chunk, unless the connection has closed, and
       * time the state update.
       */
      const commitStreamedMessages = (chat: ChatHistory, updatedMessages: ChatMessage[]) => {
        // Only update state if SSE connection is still active
        if (signal.aborted) return;

        // Performance telemetry - measure state update time
        const stateUpdateStart = performance.now();

        // Let React batch updates naturally (React 18 automatic batching)
        set({ chat: { ...chat, messages: updatedMessages } });
        performance.measure("sse-state-update", {
          start: stateUpdateStart,
          end: performance.now(),
        });
      };

      /**
       * Apply a chunk to a message already in the chat. Returns false when the
       * message was stopped and the event is ignored, and true on every other
       * path, including a chunk that leaves the content unchanged.
       */
      const applyChunkToMessage = (
        chat: ChatHistory,
        messageIndex: number,
        payload: OutputPayload,
      ): boolean => {
        const message = chat.messages[messageIndex];

        logMessageFound(message, payload.messageId);

        if (message.status === "stopped") {
          logger.info(`⏹️ Message ${payload.messageId} is stopped, ignoring SSE event`);

          return false;
        }

        const newContent = computeStreamedContent(message, payload);

        // Only update if content actually changed
        if (message.content === newContent) return true;

        const updatedMessages = [...chat.messages];

        updatedMessages[messageIndex] = { ...message, content: newContent };
        logContentUpdate(message, newContent, payload);
        commitStreamedMessages(chat, updatedMessages);

        return true;
      };

      /**
       * Store a regenerated message unless the connection has closed. Returns
       * nothing: the caller reports the replacement either way.
       */
      const commitRegeneration = (
        chat: ChatHistory,
        updatedMessages: ChatMessage[],
        replaced: ChatMessage,
        payload: OutputPayload,
      ) => {
        // Only update state if SSE connection is still active
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, skipping regeneration update");

          return;
        }

        logger.info(`📊 Updated message`, {
          id: payload.messageId,
          attempts: String(replaced.attempts || 1),
          currentAttempt: String(replaced.currentAttempt || 1),
          contentLength: String(payload.data?.length || 0),
        });

        // Let React batch updates naturally (React 18 automatic batching)
        set({ chat: { ...chat, messages: updatedMessages } });
      };

      /**
       * Reuse an assistant message for a regenerated reply, under its new ID.
       * Returns false when the target is no longer in the chat, so nothing was
       * replaced. Returns true once the replacement was built, which includes
       * an aborted connection: that skips the store update and so replaces
       * nothing, but still reports true so the caller does not go on to create
       * a second message for the same chunk.
       */
      const replaceWithRegeneration = (
        chat: ChatHistory,
        messageToReplace: ChatMessage,
        payload: OutputPayload,
      ): boolean => {
        logger.info(
          `🔄 Regeneration detected, replacing message ${messageToReplace.id} with ${payload.messageId}`,
        );

        const messageIdx = chat.messages.findIndex(
          (m: ChatMessage) => m.id === messageToReplace.id,
        );

        if (messageIdx === -1) return false;

        const updatedMessages = [...chat.messages];

        updatedMessages[messageIdx] = regeneratedMessage(messageToReplace, payload);
        logger.info(`🔄 Replaced message with regenerated content, starting progressive display`);
        commitRegeneration(chat, updatedMessages, messageToReplace, payload);

        return true;
      };

      /**
       * Handle a chunk for a message the chat does not have yet: a regeneration
       * reuses the assistant message it replaces, anything else starts a new
       * streaming message. Returns false when `replaceWithRegeneration` took
       * the chunk, which it reports even if an aborted connection left the
       * store untouched. Returns true when a new streaming message was created,
       * or would have been on a connection that is still active.
       */
      const addStreamingMessage = (chat: ChatHistory, payload: OutputPayload): boolean => {
        const { messageId, messageType, agent, data, isRegenerationEvent } = payload;
        const messageToReplace = isRegenerationEvent
          ? findRegenerationTarget(chat.messages, messageId)
          : null;

        if (!messageToReplace) {
          // Last message is user → new conversation → create new message
          logger.info(
            `💬 New conversation context detected (last message is user), creating new assistant message ${messageId}`,
          );
        } else if (replaceWithRegeneration(chat, messageToReplace, payload)) {
          return false;
        }

        // Fallback: Create new streaming message (normal flow)
        const newMessage: ExtendedChatMessage = {
          id: messageId,
          role: messageType === "user" ? "user" : "assistant",
          agent,
          content: data,
          status: "streaming",
          liked: null,
          timestamp: new Date().toISOString(),
          messageType,
        };

        logger.info(`🆕 Creating new streaming message ${messageId}`);

        // Only update state if SSE connection is still active
        if (!signal.aborted) {
          // Let React batch updates naturally (React 18 automatic batching)
          set({ chat: { ...chat, messages: [...chat.messages, newMessage] } });
        }

        return true;
      };

      /** Log the two lookups that route an output event, and find its message. */
      const findOutputTarget = (chat: ChatHistory, payload: OutputPayload): number => {
        const { messageId, chunkNumber, isRegenerationEvent } = payload;

        logger.info(`🔍 MESSAGE PROCESSING`, {
          messageId,
          chunkNumber: String(chunkNumber),
          chatId: String(chat.id),
          messageCount: String(chat.messages.length),
          isActive: String(!signal.aborted),
          isRegenerationEvent: String(isRegenerationEvent),
        });

        // Find existing message by ID
        const messageIndex = chat.messages.findIndex((m: ChatMessage) => m.id === messageId);

        logger.info(`🔎 MESSAGE LOOKUP`, {
          searchingForMessageId: messageId,
          foundAtIndex: String(messageIndex),
          messageCount: String(chat.messages.length),
          chunkNumber: String(chunkNumber),
        });

        return messageIndex;
      };

      /** Quick abort telemetry: an output event on a closed connection needs no processing. */
      const ignoreAbortedOutput = (messageProcessStart: number) => {
        logger.info("🛑 SSE connection aborted, ignoring output event");
        performance.measure("sse-message-abort", {
          start: messageProcessStart,
          end: performance.now(),
        });
      };

      const handleOutput = (event: MessageEvent) => {
        // Performance telemetry - start measurement
        const messageProcessStart = performance.now();

        // Track liveness and event ID for reconnection (ADR-0014)
        trackEvent(event);

        // Check abort signal - single source of truth for cleanup state
        if (signal.aborted) {
          ignoreAbortedOutput(messageProcessStart);

          return;
        }

        // Skip duplicate events after reconnection replay (ADR-0014)
        if (processEventIdAndCheckDuplicate(event)) return;

        const payload = parseOutputPayload(event);

        if (!payload || shouldIgnoreOutput(payload)) return;

        const chat = get().chat;

        if (!chat || signal.aborted) return;

        const messageIndex = findOutputTarget(chat, payload);

        // A chunk for a stopped message, and a regeneration that took over a
        // message (which includes one an aborted connection left unstored), are
        // deliberately not timed, so neither leaves an "sse-message-process"
        // measurement.
        const shouldRecordTiming =
          messageIndex !== -1
            ? applyChunkToMessage(chat, messageIndex, payload)
            : addStreamingMessage(chat, payload);

        if (!shouldRecordTiming) return;

        // Performance telemetry - end measurement
        performance.measure("sse-message-process", {
          start: messageProcessStart,
          end: performance.now(),
        });
      };

      /**
       * Register the artefacts and citations of a message that has just been
       * finalised, unless the connection closed meanwhile. `labels` only words
       * the log lines.
       */
      const registerFinalisedExtras = (
        content: ChatMessage["content"],
        payload: FinalisePayload,
        status: "success" | "failed" | "stopped",
        labels: { artefacts: string; citations: string },
      ) => {
        const { toolCallExecutions, messageId, citations } = payload;

        // Detect artefacts in the completed message content
        if (!signal.aborted && status === "success" && content) {
          registerCompletedMessageArtefacts(
            content,
            messageId,
            toolCallExecutions,
            labels.artefacts,
          );
        }

        // Store citations if present
        if (!signal.aborted && citations && citations.length > 0) {
          storeMessageCitations(messageId, citations, labels.citations);
        }
      };

      /** Create a new completed message (normal flow). */
      const finaliseNewMessage = (
        chat: ChatHistory,
        payload: FinalisePayload,
        status: "success" | "failed" | "stopped",
      ) => {
        const {
          toolCallExecutions,
          toolCalls,
          usage,
          data,
          agent,
          messageId,
          messageType,
          citations,
        } = payload;

        logger.info(`🆕 Creating new completed message with backend ID ${messageId}`);
        const newMessage: ExtendedChatMessage = {
          id: messageId,
          role: messageType === "user" ? "user" : "assistant",
          agent,
          content: data,
          status: status,
          liked: null,
          timestamp: new Date().toISOString(),
          toolCallExecutions: toolCallExecutions || EMPTY_TOOL_EXECUTIONS,
          toolCalls: toolCalls || EMPTY_TOOL_CALLS,
          usage,
          messageType,
          ...(citations && { citations }), // Add citations if present
        };

        // Only update state if SSE connection is still active
        if (!signal.aborted) {
          set({ chat: { ...chat, messages: [...chat.messages, newMessage] } });
        }

        registerFinalisedExtras(data, payload, status, {
          artefacts: "new message",
          citations: "message",
        });
      };

      /** The streaming message `existing`, completed with the data of a completion event. */
      const completeStreamingMessage = (
        existing: ChatMessage,
        payload: FinalisePayload,
        status: "success" | "failed" | "stopped",
      ): ExtendedChatMessage => {
        const { toolCallExecutions, toolCalls, usage, data, messageType, citations } = payload;

        return {
          ...existing,
          // When finalising, preserve existing content if new data is empty
          content: data || existing.content,
          status: status,
          toolCallExecutions:
            toolCallExecutions || existing.toolCallExecutions || EMPTY_TOOL_EXECUTIONS,
          toolCalls: toolCalls || existing.toolCalls || EMPTY_TOOL_CALLS,
          usage,
          // Preserve messageType from SSE or existing message
          messageType: messageType || (existing as ExtendedChatMessage).messageType,
          ...(citations && { citations }), // Add citations if present
        };
      };

      /**
       * Update an existing streaming message to completed. Returns false when
       * the message is not streaming, which skips finalisation entirely.
       */
      const finaliseStreamingMessage = (
        chat: ChatHistory,
        messageIndex: number,
        payload: FinalisePayload,
        status: "success" | "failed" | "stopped",
      ): boolean => {
        const { messageId } = payload;

        logger.info(
          `✅ Finalization: Found existing message ${messageId} at index ${messageIndex}`,
        );
        const message = chat.messages[messageIndex];

        if (message.status !== "streaming") {
          logger.info(
            `⚠️ Message ${messageId} is not streaming (status: ${message.status}), skipping finalization`,
          );

          return false;
        }

        const completed = completeStreamingMessage(message, payload, status);
        const updatedMessages = [...chat.messages];

        updatedMessages[messageIndex] = completed;

        // Only update state if SSE connection is still active
        if (!signal.aborted) {
          set({ chat: { ...chat, messages: updatedMessages } });
        }

        registerFinalisedExtras(completed.content, payload, status, {
          artefacts: "message",
          citations: "updated message",
        });

        return true;
      };

      /** Save the chat in the background (asynchronously, to avoid infinite loops). */
      const scheduleBackgroundSave = (updatedChat: ChatHistory) => {
        setTimeout(async () => {
          if (!signal.aborted && get().chat?.id === updatedChat.id) {
            try {
              await get().save(adminClient, updatedChat);
            } catch (error) {
              logger.error("❌ Background save failed", error);
            }
          }
        }, 0);
      };

      /**
       * After the first successful assistant reply, refresh the chat history
       * list. Nothing happens for any other reply, or without a refresh callback.
       */
      const scheduleHistoryRefresh = (
        updatedChat: ChatHistory,
        status: "success" | "failed" | "stopped",
      ) => {
        // Trigger auto-save mechanism for successful messages (only if connection is still active)
        if (
          signal.aborted ||
          typeof fetchChatHistory !== "function" ||
          typeof userId !== "string" ||
          status !== "success"
        ) {
          return;
        }

        const assistantSuccessMessages = updatedChat.messages.filter(
          (m) => m.role === "assistant" && m.status === "success",
        );

        if (assistantSuccessMessages.length !== 1) return;

        logger.info("🔄 Triggering fetchChatHistory after message completion");
        // Wrap in setTimeout to avoid state updates during render
        setTimeout(() => {
          if (!signal.aborted) {
            try {
              fetchChatHistory(userId, adminClient);
            } catch (error) {
              // Silently handle errors from fetchChatHistory to prevent unmounted component updates
              logger.warn("⚠️ fetchChatHistory failed (component may be unmounted)", {
                error: String(error),
              });
            }
          }
        }, 100);
      };

      /**
       * Save the chat in the background and, after the first successful
       * assistant reply, refresh the chat history list.
       */
      const saveAfterFinalise = (status: "success" | "failed" | "stopped") => {
        // Only proceed with save operations if SSE connection is still active
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, skipping save operations");

          return;
        }

        const updatedChat = get().chat;

        if (!updatedChat) {
          throw new Error("Chat is required");
        }

        scheduleBackgroundSave(updatedChat);
        scheduleHistoryRefresh(updatedChat, status);
      };

      const finalizeMessage = (
        event: MessageEvent,
        status: "success" | "failed" | "stopped" = "success",
      ) => {
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring finalize event");

          return;
        }

        const payload = parseFinalisePayload(event);

        if (!payload) return;

        const chat = get().chat;

        if (!chat || signal.aborted) return;

        logger.info(`🏁 Finalizing message ${payload.messageId}`);

        const messageIndex = chat.messages.findIndex(
          (m: ChatMessage) => m.id === payload.messageId,
        );

        if (messageIndex === -1) {
          finaliseNewMessage(chat, payload, status);
        } else if (!finaliseStreamingMessage(chat, messageIndex, payload, status)) {
          return;
        }

        saveAfterFinalise(status);
      };

      const handleWorkflowCompletion = (event: MessageEvent) => {
        trackEvent(event);
        if (processEventIdAndCheckDuplicate(event)) return;
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring workflow completion");

          return;
        }

        // Parse the event to check for stopped/cancelled status and messages_discarded flag
        let eventData;

        try {
          eventData = JSON.parse(event.data);
        } catch (e) {
          logger.warn("Failed to parse workflow completion event", { error: String(e) });
          eventData = {};
        }

        const status = eventData.status;

        // If the workflow was stopped by user, mark the message as "stopped" but keep it visible
        // Messages will be removed when user sends a new message (handled in invoke function)
        if (status === "stopped" || status === "cancelled") {
          // Finalize with "stopped" status to show partial response with stopped indicator
          finalizeMessage(event, "stopped");
          set({ streaming: false });

          // Close connection
          cleanup();

          return;
        }

        set({ streaming: false });
        finalizeMessage(event, "success");

        // Close connection immediately to prevent eventsource polyfill auto-reconnect errors
        // Will be re-opened if user triggers regeneration
        logger.info("🔌 Closing SSE connection after workflow completion");
        cleanup();
      };

      const handleMessageCompletion = (event: MessageEvent) => {
        trackEvent(event);
        if (processEventIdAndCheckDuplicate(event)) return;
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring message completion");

          return;
        }

        // Parse event data to check for regeneration flag
        let parsedData;

        try {
          parsedData = JSON.parse(event.data);
        } catch (e) {
          logger.error("Failed to parse message completion event data", e);
          parsedData = {};
        }

        const messageId = extractMessageId(parsedData);
        const isRegenerationComplete =
          parsedData.is_regeneration || parsedData.data?.is_regeneration;

        if (isRegenerationComplete && messageId) {
          logger.info(`🔄 Regeneration completion for message ${messageId}`);

          // DON'T update message status to success yet - let SSE streaming complete first
          // The message should remain in "streaming" status until handleFinalize is called
          logger.info(
            `🔄 Regeneration content received for message ${messageId}, keeping streaming status for SSE`,
          );

          const chat = get().chat;

          if (chat) {
            // Keep the message in streaming status, just update metadata like attempts
            const updatedMessages = chat.messages.map((msg) => {
              if (msg.id === messageId) {
                // Keep status as "streaming" to allow stop button to work during SSE streaming
                return { ...msg, status: "streaming" as const };
              }

              return msg;
            });

            set({ chat: { ...chat, messages: updatedMessages } });
          }
        }

        // Set streaming to false to allow user to send input or next message
        // But keep SSE connection open for multi-agent workflows
        set({ streaming: false });

        logger.info(
          `✅ Message ${messageId} completed, streaming set to false but SSE connection remains open for workflow continuation`,
        );

        finalizeMessage(event);

        // For simple chat_agent workflow, close the connection after message completes
        // For complex multi-agent workflows, keep connection open until workflow_completed
        const { chatWorkflowId } = get();

        // Parse event to check if this is a routing/system message
        // If so, keep connection open - more messages are expected
        let isRoutingMessage = false;

        try {
          const eventData = JSON.parse(event.data);
          const msgType = eventData.message_type || eventData.data?.message_type;
          const msgId = eventData.message_id || eventData.data?.message_id || "";

          isRoutingMessage = msgType === "system" || msgId.startsWith("routing_");
        } catch {
          // Ignore parse errors
        }

        if (isRoutingMessage) {
          logger.info(
            "🔗 Keeping SSE connection open after routing message - main response expected",
          );
        } else if (chatWorkflowId === "chat_agent") {
          logger.info("🔌 Closing SSE connection after message completion (chat_agent workflow)");
          cleanup();
        } else {
          logger.info("🔗 Keeping SSE connection open for multi-agent workflow continuation");
        }
      };

      const handleError = (event: MessageEvent) => {
        trackEvent(event);
        // Errors are deduplicated to prevent duplicate error messages and
        // double cleanup() calls when error events are replayed after reconnect
        if (processEventIdAndCheckDuplicate(event)) return;
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring error event");

          return;
        }

        if (!event.data) return;
        const chat = get().chat;

        if (!chat || signal.aborted) return;

        const msg: ChatMessage = {
          id: String(Date.now()),
          role: "assistant",
          content: event.data || "An error occurred during the conversation",
          status: "failed",
          liked: null,
          timestamp: new Date().toISOString(),
        };

        // Only update state if SSE connection is still active
        if (!signal.aborted) {
          // Stop streaming indicator when error occurs
          set({ chat: { ...chat, messages: [...chat.messages, msg] }, streaming: false });
          // Defer save to avoid state updates during error handling
          setTimeout(() => {
            if (!signal.aborted) {
              get().save(adminClient, { ...chat, messages: [...chat.messages, msg] });
            }
          }, 0);
        }
        cleanup();
      };

      const handleRegenerationStarted = (event: MessageEvent) => {
        trackEvent(event);
        if (processEventIdAndCheckDuplicate(event)) return;
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring regeneration_started event");

          return;
        }

        let msg;

        try {
          msg = JSON.parse(event.data);
        } catch (error) {
          logger.error("Failed to parse regeneration_started event", error);

          return;
        }

        const data = msg.data || msg;
        const messageId = extractMessageId(data);
        const strategy = data.strategy;
        const attempt = data.attempt;
        const timestamp = data.timestamp;

        logger.info(`🔄 Regeneration started for message ${messageId}`, {
          strategy: String(strategy),
          attempt: String(attempt),
          timestamp: String(timestamp),
        });

        const chat = get().chat;

        if (!chat) return;

        // Find and update the message to show regeneration has started
        const messageIndex = chat.messages.findIndex((m: ChatMessage) => m.id === messageId);

        if (messageIndex !== -1) {
          const updatedMessages = [...chat.messages];

          updatedMessages[messageIndex] = {
            ...updatedMessages[messageIndex],
            status: "streaming",
            content: "", // Clear content to prepare for new regenerated content
          };

          logger.info(`🔄 Message ${messageId} marked as streaming (regeneration started)`);

          // Let React batch updates naturally (React 18 automatic batching)
          set({ chat: { ...chat, messages: updatedMessages } });
        }
      };

      const handleInputRequest = (event: MessageEvent) => {
        trackEvent(event);
        if (processEventIdAndCheckDuplicate(event)) return;
        if (signal.aborted) {
          logger.info("🛑 SSE connection inactive, ignoring input request");

          return;
        }

        let msg;

        try {
          msg = JSON.parse(event.data);
        } catch (error) {
          logger.error("Failed to parse input request", error);

          return;
        }

        const messageId = msg.message_id;
        const question = msg.data;
        const agent = msg.agent;

        logger.info("🎤 Input request received", {
          messageId,
          question: question?.substring(0, 100) || "[empty]",
          agent: String(agent),
        });

        // Store the input request state so the next user message is sent as input
        // The input request itself is NOT a message - it's just a signal that input is needed
        // The actual question/prompt should come through normal output events if it needs to be displayed
        set({
          pendingInputRequest: {
            messageId,
            question,
            agent,
            timestamp: new Date().toISOString(),
          },
        });

        logger.info("✅ Input request stored - waiting for user response");
        logger.info(
          "💡 If question needs to be displayed, it should come via normal output events",
        );
      };

      // Register event handlers on the initial EventSource
      registerEventListeners(currentSe);

      // Return cleanup function for external cleanup if needed
      return cleanup;
    },

    submitFeedback: async (adminClient, runId, messageId, liked, feedback) => {
      if (!adminClient) return;
      await adminClient?.chatHistories.setFeedback({
        feedback: feedback,
        runId: runId,
        messageId: messageId,
        liked: liked,
      });
    },

    load: async (adminClient, runId) => {
      logger.info("🔍 ChatStore: Loading chat with runId", {
        runId: String(runId),
        runIdType: typeof runId,
        runIdLength: String(runId?.length),
        containsSpaces: String(runId?.includes(" ")),
      });

      if (!adminClient || !runId) return;
      if (runId === get().runId) {
        return;
      }

      // Set loading state immediately
      set({ chatLoading: true });

      // Clean up any active SSE connections from previous chat before loading new one
      const currentRunId = get().runId;

      if (currentRunId) {
        const activeConnections = (window as any).__activeSSEConnections || {};
        const connection = activeConnections[currentRunId];

        if (connection) {
          logger.info("🛑 Cleaning up previous SSE connection when loading new chat", {
            runId: String(currentRunId),
          });

          // Abort the connection using AbortController
          if (connection.abortController) {
            connection.abortController.abort();
          }

          // Call cleanup function
          if (typeof connection.cleanup === "function") {
            connection.cleanup();
          }

          // Remove from registry
          delete activeConnections[currentRunId];
        }
      }

      logger.info("💭 Loading chat history", { runId: String(runId) });

      // Clear any existing artefacts and citations from previous chat
      useArtefactStore.getState().clearArtefacts();
      useArtefactStore.getState().clearCitations();

      // Force hide panel when loading from history (override localStorage state)
      const artefactStore = useArtefactStore.getState();

      if (artefactStore.artefactsVisible) {
        artefactStore.toggleArtefacts();
      }

      try {
        const chatHistory = await fetchChatHistoryWithFallback(adminClient, runId);

        // Ensure messages have the correct structure
        if (chatHistory && chatHistory.messages) {
          const messagesWithTools = chatHistory.messages.map(normaliseLoadedMessage);

          set({
            chat: {
              ...chatHistory,
              messages: messagesWithTools,
            },
            streaming: false,
            chatLoading: false,
            runId: chatHistory.id,
            chatWorkflowId: chatHistory.workflowType || "chat_agent",
          });

          registerLoadedMessages(messagesWithTools);

          logger.info(`✅ Chat loaded successfully with ${messagesWithTools.length} messages`);
        } else {
          // Mirror the success-path set() above: restoring chatWorkflowId
          // from the loaded chat is what gates RegenerateButton rendering
          // in MessageCard. Without it, the retry button never appears after
          // a navigation/reload, and regeneration tests fail with "retry
          // button not found" even though the chat is intact.
          set({
            chat: chatHistory,
            streaming: false,
            chatLoading: false,
            runId: chatHistory.id,
            chatWorkflowId: chatHistory.workflowType || "chat_agent",
          });

          logger.info("✅ Chat loaded successfully (no messages)");
        }
      } catch (error) {
        logger.error("❌ Error loading chat history", error);

        // Don't reset to null - this breaks the UI. Instead, create minimal chat structure
        const fallbackChat = {
          id: runId,
          messages: [],
          timestamp: new Date().toISOString(),
          tenant: "unknown",
          username: "unknown",
          modelUsed: "unknown",
          summary: "Failed to load chat history",
          metrics: { usage: {} },
        };

        set({
          chat: fallbackChat,
          streaming: false,
          chatLoading: false,
          runId: runId,
        });

        logger.info("⚠️ Created fallback chat structure for", { runId: String(runId) });
      }
    },

    updateMessageFeedback: (messageId: string, liked: boolean | null) => {
      const chat = get().chat;

      if (!chat) return;

      const updatedMessages = chat.messages.map((message: ChatMessage) =>
        message.id === messageId ? { ...message, liked } : message,
      );

      set({ chat: { ...chat, messages: updatedMessages } });
    },

    updateMessageComment: (messageId: string, feedback: string) => {
      const chat = get().chat;

      if (!chat) return;

      const updatedMessages = chat.messages.map((message: ChatMessage) =>
        message.id === messageId ? { ...message, feedback } : message,
      );

      set({ chat: { ...chat, messages: updatedMessages } });
    },

    updateMessage: (chatId: string, messageId: string, updates: Partial<ChatMessage>) => {
      const chat = get().chat;

      if (!chat || chat.id !== chatId) return;

      const updatedMessages = chat.messages.map((message: ChatMessage) =>
        message.id === messageId ? { ...message, ...updates } : message,
      );

      set({ chat: { ...chat, messages: updatedMessages } });
    },
  };
}); // Added missing closing for the return statement

export { useChatStore };
